import Button from "@/app/components/Button";
import Link from "next/link";
import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import React from "react";
import type { ProductOption } from "@/app/types/domain";

interface ProductCardProps {
  productId: string;
  title: string;
  desc: string;
  img: string;
  price?: string;
  productObj?: ProductOption;
  offer?: boolean;
  offerMessage?: string;
  onBuy?: (product?: ProductOption) => void;
  onSell?: (product?: ProductOption) => void;
  onExchange?: (product?: ProductOption) => void;
  allowedActions?: ("buy" | "sell" | "exchange")[];
}

const MAX_DESC_LENGTH = 160;

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const ProductCard: React.FC<ProductCardProps> = ({
  productId,
  title,
  desc,
  img,
  price,
  productObj,
  offer,
  offerMessage,
  onBuy,
  onSell,
  onExchange,
  allowedActions,
}) => {
  const productRef = productObj ?? { id: productId, name: title };
  const t = useTranslations("dashboard");
  const tCard = useTranslations("productCard");
  const currentLocale = useLocale();
  const router = useRouter();
  const productSlug = slugify(title || productId || "product");
  const productHref =
    currentLocale === "en"
      ? `/products/${productSlug || productId}`
      : `/${currentLocale}/products/${productSlug || productId}`;
  const displayDesc =
    desc.length > MAX_DESC_LENGTH
      ? desc.slice(0, MAX_DESC_LENGTH) + "..."
      : desc;

  const showBuy = allowedActions?.includes("buy") ?? true;
  const showSell = allowedActions?.includes("sell") ?? true;
  const showExchange = allowedActions?.includes("exchange") ?? true;
  const activeCount = [showBuy, showSell, showExchange].filter(Boolean).length;

  const BUY_ICON = "/logo/buy_logo.svg";
  const SELL_ICON = "/logo/sell_logo.svg";
  const EXCHANGE_ICON = "/logo/exchange_logo.svg";

  const buyBtn = showBuy && (
    <Button
      label={t("buy")}
      color="blue"
      icon={BUY_ICON}
      className="flex-1 lg:px-4 lg:py-1.5 lg:text-xs xl:px-6 xl:py-2 xl:text-sm px-6 py-2 text-sm rounded-2xl cursor-pointer"
      onClick={() => onBuy?.(productRef)}
    />
  );
  const sellBtn = showSell && (
    <Button
      label={t("sell")}
      color="pink"
      icon={SELL_ICON}
      className="flex-1 lg:px-4 lg:py-1.5 lg:text-xs xl:px-6 xl:py-2 xl:text-sm px-6 py-2 text-sm rounded-2xl cursor-pointer"
      onClick={() => onSell?.(productRef)}
    />
  );
  const exchangeBtn = showExchange && (
    <Button
      label={t("exchange")}
      color="yellow"
      icon={EXCHANGE_ICON}
      className="flex-1 lg:px-4 lg:py-1.5 lg:text-xs xl:px-6 xl:py-2 xl:text-sm px-6 py-2 text-sm rounded-2xl cursor-pointer"
      onClick={() => onExchange?.(productRef)}
    />
  );
  return (
    <div
      className={`w-72 min-w-65 max-w-90 flex-shrink-0${offer ? " offer-card-wrapper" : " product-card-float"}`}
    >
      <div
        className="w-full bg-white rounded-3xl flex flex-col overflow-hidden cursor-pointer"
        style={{ minHeight: 420 }}
        onClick={() => router.push(productHref)}
      >
        <div className="w-full shrink-0 relative" style={{ height: 180 }}>
          <img src={img} alt={title} className="w-full h-full object-contain" />
          {offer && (
            <div className="offer-badge-anim absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-xl shadow-md select-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3.5 h-3.5 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z"
                  clipRule="evenodd"
                />
              </svg>
              OFFER
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3.5 h-3.5 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
          {offer && (
            <div className="offer-badge-anim absolute top-40.5 left-2.5 z-10 flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[14px] font-bold px-2.5 py-1 rounded-2xl shadow-md select-none">
              {offerMessage || "Special Offer 🔥"}
            </div>
          )}

          <Link
            href={productHref}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-2.5 right-2.5 bg-white/90 hover:bg-white text-[#0A2540] text-xs font-semibold px-3 py-1.5 rounded-xl shadow-sm transition-colors duration-150 cursor-pointer"
          >
            {tCard("viewDetails")}
          </Link>
        </div>

        <div className="flex flex-col flex-1 px-5 pt-4 pb-5">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-base capitalize font-bold text-[#0A2540] leading-tight">
              {title}
            </h3>
            {price && parseFloat(price) > 0 && (
              <span className="shrink-0 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-0.5 whitespace-nowrap">
                ₹
                {parseFloat(price).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
                /kg
              </span>
              // </div>
            )}
          </div>

          <p className="text-[#64748B] capitalize text-sm leading-snug flex-1">
            {displayDesc}
          </p>
          <div
            className="flex flex-col items-center gap-2 mt-4"
            onClick={(e) => e.stopPropagation()}
          >
            {activeCount === 3 ? (
              <>
                <div className="w-full">{buyBtn}</div>
                <div className="w-full flex gap-2">
                  {sellBtn}
                  {exchangeBtn}
                </div>
              </>
            ) : (
              <div className="w-full flex gap-2">
                {buyBtn}
                {sellBtn}
                {exchangeBtn}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    // </div>
  );
};

export default ProductCard;
